import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useLanguageTranslator } from "../../../components";
import { useRef, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import { toastHandler } from "../../../utils/helpers";
import { toastTypesKeys } from "../../../utils/constants";
import { ToastContainer } from "react-toastify";

type OtpInputProps = {
  length?: number;
  onChange?: (otp: string) => void;
  autoFocus?: boolean;
};

function OtpVerification() {
  const handleOtpChange = (value: string) => {
    console.log("OTP:", value);
  };

  return <OtpInput length={6} onChange={handleOtpChange} />;
}

const otpVerificationQuery = gql`
  mutation VerifyOtpCode($otpCode: String!) {
    verifyOtpCode(input: { otpCode: $otpCode }) {
      clientMutationId
      message
      passwordToken
    }
  }
`;

function OtpInput({ length = 6, onChange, autoFocus = true }: OtpInputProps) {
  const [otp, setOtp] = useState<string[]>(Array.from({ length }, () => ""));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const { translator } = useLanguageTranslator();
  const { resetPasswordToken, passwordToken } = useAuthSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [otpVerification, { data, loading, error }] =
    useMutation(otpVerificationQuery);

  const notify = (next: string[]) => {
    setOtp(next);
    onChange?.(next.join(""));
  };

  const handleChange = (e: React.ChangeEvent<any>, idx: number) => {
    const val = e.target.value.replace(/\D/g, ""); // only digits
    if (!val && otp[idx] === "") return; // nothing changed

    const next = [...otp];
    // take only first character (in case of paste single char)
    next[idx] = val.charAt(0) ?? "";
    notify(next);

    // if filled, move focus to next (unless last)
    if (val && idx < length - 1) {
      inputsRef.current[idx + 1]?.focus();
      inputsRef.current[idx + 1]?.select();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    const key = e.key;

    if (key === "Backspace") {
      e.preventDefault(); // prevent default to control behavior precisely

      if (otp[idx]) {
        // If current box has a value -> clear it and keep focus here
        const next = [...otp];
        next[idx] = "";
        notify(next);
      } else if (idx > 0) {
        // If current is empty -> move to previous, clear it and focus it
        const prev = idx - 1;
        const next = [...otp];
        next[prev] = "";
        notify(next);
        const prevInput = inputsRef.current[prev];
        if (prevInput) {
          prevInput.focus();
          prevInput.select();
        }
      }
    } else if (key === "ArrowLeft") {
      e.preventDefault();
      if (idx > 0) inputsRef.current[idx - 1]?.focus();
    } else if (key === "ArrowRight") {
      e.preventDefault();
      if (idx < length - 1) inputsRef.current[idx + 1]?.focus();
    }
  };

  // handle paste: accept up to `length` digits and fill sequentially
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!paste) return;

    const next = [...otp];
    for (let i = 0; i < length; i++) {
      const char = paste.charAt(i);
      if (!char) break;
      next[i] = char;
    }
    notify(next);

    // focus first empty or last
    const firstEmpty = next.findIndex((c) => c === "");
    const focusIndex = firstEmpty === -1 ? length - 1 : firstEmpty;
    inputsRef.current[focusIndex]?.focus();
    inputsRef.current[focusIndex]?.select();
  };

  const isComplete = otp.every((val) => val !== "");

  async function handleSubmit() {
    try {
      const response = await otpVerification({
        variables: { otpCode: otp.join("") },
      });
      if (response?.data?.verifyOtpCode?.message) {
        await resetPasswordToken(response?.data?.verifyOtpCode?.passwordToken);
        toastHandler(
          response?.data?.verifyOtpCode?.message || "",
          toastTypesKeys.success
        );
        setTimeout(() => {
          navigate(`/auth/new-password`);
        }, 2000);
        return;
      }
    } catch (err) {
      console.error("Error during mutation:", err);
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }

  const [local, domain] = location?.state?.email.split("@");
  const shortLocal = local.slice(-4);
  const shortEmail = shortLocal + "@" + domain;

  return (
    <>
      <ToastContainer />
      <Stack
        sx={{ height: "100%" }}
        alignItems={"center"}
        justifyContent="center"
      >
        <Box display="flex" justifyContent="center" mb={2}>
          <SmartPhoneIcon />
        </Box>
        <Box textAlign="center" mb={3}>
          <Typography variant="h5" color="text.primary" gutterBottom>
            {translator(`Two-Factor Verification`)}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={1}>
            {translator(`Enter the verification code we sent to`)}
          </Typography>
          <Typography variant="h6" color="text.primary">
            ******{shortEmail || ""}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {Array.from({ length }).map((_, i) => (
            <TextField
              key={i}
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: { textAlign: "center", padding: "20px 12px" },
              }}
              value={otp[i]}
              onChange={(e) => handleChange(e, i)}
              onKeyDown={(e) =>
                handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, i)
              }
              onPaste={handlePaste}
              variant="outlined"
              size="small"
              sx={{
                width: 70,
                marginBottom: "30px",
                borderRadius: "10px",
              }}
              inputRef={(el) => (inputsRef.current[i] = el)}
              autoFocus={autoFocus && i === 0}
            />
          ))}
        </Box>
        <Box display="flex" justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={!isComplete}
            sx={{ minWidth: "120px" }}
          >
            {translator("Submit")}
          </Button>
        </Box>
      </Stack>
    </>
  );
}

export { OtpVerification };

function SmartPhoneIcon() {
  return (
    <svg
      width="162"
      height="162"
      viewBox="0 0 162 162"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M49.4498 17.217C48.1718 17.217 46.9262 17.667 46.046 18.5454C45.164 19.4202 44.714 20.6748 44.714 21.9492V140.123C44.714 141.397 45.164 142.643 46.046 143.525C46.9262 144.407 48.1718 144.857 49.4498 144.857H112.666C113.94 144.857 115.197 144.407 116.068 143.525C116.952 142.643 117.4 141.397 117.4 140.123V21.9492C117.4 20.6748 116.95 19.4202 116.068 18.5454C115.197 17.6652 113.94 17.217 112.666 17.217H102.906C102.138 17.217 101.454 17.676 101.164 18.387L99.7094 21.9492C99.2396 23.1138 98.867 23.6844 98.273 24.0696C97.6934 24.4638 96.662 24.7914 94.637 24.7914H67.475C65.4518 24.7914 64.4204 24.462 63.839 24.0696C63.2486 23.6862 62.8724 23.1156 62.4044 21.9492L60.9518 18.387C60.6674 17.6832 59.9834 17.217 59.2256 17.217H49.4498Z"
        fill="#9EDCFF"
      />
      <path
        d="M49.1649 13.176H112.946C117.55 13.176 121.185 16.83 121.185 21.4254V140.647C121.185 145.24 117.549 148.898 112.946 148.898H49.1649C44.5641 148.898 40.9245 145.24 40.9245 140.647V21.4254C40.9263 16.8318 44.5641 13.176 49.1649 13.176ZM49.1649 9.44458C42.5589 9.44458 37.1841 14.8194 37.1841 21.4254V140.647C37.1841 147.256 42.5589 152.627 49.1649 152.627H112.946C119.556 152.627 124.936 147.256 124.936 140.647V21.4254C124.936 14.8194 119.556 9.44458 112.946 9.44458H49.1649Z"
        fill="#7E94B5"
      />
      <path
        d="M75.0235 17.217C73.9813 17.217 73.1497 18.0486 73.1497 19.0908C73.1497 20.1312 73.9813 20.9664 75.0235 20.9664H88.0357C89.0671 20.9664 89.9113 20.1312 89.9113 19.0908C89.9113 18.0486 89.0671 17.217 88.0357 17.217H75.0235Z"
        fill="#B5B5C3"
      />
    </svg>
  );
}
